import React, { useEffect, useRef, useState } from "react";

/**
 * Carrusel de 3 chips con animación "deck 3D": el chip activo se ve
 * de frente, los siguientes esperan detrás levemente rotados y con
 * menor opacidad. Al avanzar, el activo sale girando en Y (flip) y el
 * siguiente entra desde el fondo.
 *
 * Pausa automática mientras el mouse está encima del contenedor —
 * usamos un flag `isPaused` que impide el tick del setInterval. Cada
 * chip tiene imagen de fondo, título (eyebrow) y texto grande.
 *
 * Props:
 *   chips: [{ image_url, title, text }, ...] — exactamente 3.
 *   intervalMs: tiempo entre cambios (default 4500ms).
 */
export default function LandingChipCarousel({ chips = [], intervalMs = 4500 }) {
  const [active, setActive] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  // Guardamos `active` en un ref para que el interval no se tenga que
  // recrear cada vez que cambia — evita el "jitter" clásico cuando
  // combinas `setInterval` + `useState` dentro del mismo effect.
  const activeRef = useRef(0);
  useEffect(() => { activeRef.current = active; }, [active]);

  useEffect(() => {
    if (!chips.length || chips.length < 2) return;
    const t = setInterval(() => {
      if (!isPaused) {
        setActive((a) => (a + 1) % chips.length);
      }
    }, intervalMs);
    return () => clearInterval(t);
  }, [chips.length, intervalMs, isPaused]);

  if (!chips || chips.length === 0) return null;

  // Dado un índice, calcula su "rol" visual relativo al activo:
  //   0 = al frente, 1 = atrás-a-la-derecha, 2 = atrás-más-atrás.
  // Para 3 chips, cada uno ocupa un rol distinto y rotan.
  const roleOf = (i) => {
    const n = chips.length;
    return ((i - active) % n + n) % n;
  };

  return (
    <div
      className="relative w-full aspect-[4/5] rounded-3xl select-none"
      style={{ perspective: "1200px" }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setTimeout(() => setIsPaused(false), 1500)}
      data-testid="landing-chip-carousel"
    >
      {chips.map((chip, i) => {
        const role = roleOf(i);
        // Estilos por rol: el 0 al frente visible; los demás detrás,
        // desplazados y oscurecidos. Transición 3D con rotateY para
        // una sensación de "deck" moderna.
        const isFront = role === 0;
        const translateX = role === 0 ? 0 : role === 1 ? 18 : -14;
        const translateY = role === 0 ? 0 : role === 1 ? 22 : 44;
        const scale = role === 0 ? 1 : role === 1 ? 0.94 : 0.88;
        const rotateY = role === 0 ? 0 : role === 1 ? -16 : 18;
        const opacity = role === 0 ? 1 : role === 1 ? 0.55 : 0.3;
        const z = role === 0 ? 30 : role === 1 ? 20 : 10;
        return (
          <div
            key={i}
            className={`absolute inset-0 rounded-3xl overflow-hidden shadow-2xl transition-all duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${isFront ? "cursor-default" : "cursor-pointer"}`}
            style={{
              transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale}) rotateY(${rotateY}deg)`,
              opacity,
              zIndex: z,
              transformStyle: "preserve-3d",
            }}
            onClick={() => { if (!isFront) setActive(i); }}
            data-testid={`landing-chip-${i}`}
            data-active={isFront ? "true" : "false"}
          >
            <img
              src={chip.image_url}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/65 via-black/15 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 text-white">
              <p className="text-xs uppercase tracking-widest opacity-80" data-testid={`landing-chip-${i}-title`}>
                {chip.title}
              </p>
              <p className="font-display font-extrabold text-4xl md:text-5xl mt-1 leading-[1.02]" data-testid={`landing-chip-${i}-text`}>
                {chip.text}
              </p>
            </div>
          </div>
        );
      })}

      {/* Indicadores / dots. Clickeables: seleccionan ese chip. */}
      <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5" style={{ zIndex: 40 }}>
        {chips.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Ir al chip ${i + 1}`}
            onClick={() => setActive(i)}
            className={`h-1.5 rounded-full transition-all duration-500 ${i === active ? "w-8 bg-white" : "w-1.5 bg-white/45 hover:bg-white/70"}`}
            data-testid={`landing-chip-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}
